// ---------------------------------------------------------------------------
// Rearranging a board in front of one player's eyes.
//
// This shuffles *positions*, never tiles. The board array the server sent is the
// only thing that says which letter is which, and the answer builder consumes it
// by index, so a permutation of that array would silently rewrite the word a
// player is halfway through typing. What moves is a list of board indices
// saying, for each slot on screen, which tile to draw there. The identity of
// every tile survives untouched, which is the whole safety argument.
//
// The randomness arrives as an argument rather than being drawn here. The engine
// takes the same shape for the same reason: a function that reaches for
// `Math.random()` itself can only be tested by running it a thousand times and
// hoping.
// ---------------------------------------------------------------------------

/** The board as drawn: slot i shows tile i. */
export function identityOrder(count: number): number[] {
  return Array.from({ length: count }, (_, index) => index);
}

function sameOrder(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/**
 * How many draws before giving up on chance and forcing the issue.
 *
 * Fisher-Yates over nine items returns the identity about one time in 362,880,
 * and a button that visibly does nothing reads as broken rather than as unlucky.
 * Eight draws makes an accidental no-op impossible in practice; the rotation
 * below covers the degenerate source that would otherwise loop forever.
 */
const MAX_DRAWS = 8;

/**
 * A new display order, guaranteed to differ from the one passed in.
 *
 * `random` must behave like `Math.random`: [0, 1). It is clamped anyway, because
 * a stub that returns exactly 1 would otherwise index off the end of the array.
 */
export function shuffleOrder(order: number[], random: () => number): number[] {
  if (order.length < 2) return [...order];

  for (let draw = 0; draw < MAX_DRAWS; draw += 1) {
    const next = [...order];
    for (let index = next.length - 1; index > 0; index -= 1) {
      const pick = Math.floor(random() * (index + 1));
      const swap = Math.min(Math.max(pick, 0), index);
      [next[index], next[swap]] = [next[swap], next[index]];
    }
    if (!sameOrder(next, order)) return next;
  }

  // Every draw came back as the order we started with. A rotation by one is not
  // a shuffle, but it is guaranteed to move, and moving is what was asked for.
  return [...order.slice(1), order[0]];
}
